import React from "react";
import { motion } from "framer-motion";
import { Building, Hotel, ShoppingBag, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { RESIDENCE_IMAGE, THERMAL_IMAGE } from "../data/media";

const ICONS = [Building, Hotel, ShoppingBag, Sparkles];

export const ResidenceHotel = () => {
  const { t } = useLang();

  return (
    <section id="residence" className="relative py-24 md:py-32 bg-[#0D0D0D] overflow-hidden grain" data-testid="residence-section">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Vertical tower image */}
          <Reveal>
            <div className="relative">
              <div className="aspect-[3/4] max-h-[640px] overflow-hidden border border-[#2E2E2E]">
                <motion.img
                  src={RESIDENCE_IMAGE}
                  alt="Phantom Residence tower"
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <div className="absolute top-6 end-6 border border-[#D4AF37] bg-[#0A0A0A]/90 backdrop-blur px-4 py-2">
                <span className="text-xs uppercase tracking-[0.18em] text-[#D4AF37]">{t.residence.ramadaNote}</span>
              </div>
            </div>
          </Reveal>

          {/* Content */}
          <div>
            <Reveal>
              <span className="overline">{t.residence.overline}</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.12] tracking-tight text-[#FFFFF0]">
                {t.residence.title}
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 text-base leading-relaxed text-[#FFFFF0]/70 font-light">{t.residence.text}</p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-4 text-base leading-relaxed text-[#FFFFF0]/60 font-light">{t.residence.text2}</p>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#2E2E2E] border border-[#2E2E2E]">
              {t.residence.floors.map((f, i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={i} delay={0.06 * i}>
                    <div className="bg-[#0A0A0A] p-5 h-full" data-testid={`residence-floor-${i}`}>
                      <Icon size={22} strokeWidth={1} className="text-[#D4AF37]" />
                      <p className="mt-3 font-serif text-xl text-[#FFFFF0]">{f.name}</p>
                      <p className="mt-1 text-xs text-[#FFFFF0]/55 font-light">{f.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        {/* Thermal banner */}
        <Reveal delay={0.1}>
          <div className="mt-16 relative overflow-hidden border border-[#2E2E2E]">
            <img src={THERMAL_IMAGE} alt="Thermal hotel concept" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
            <div className="relative px-8 md:px-14 py-16 md:py-20 max-w-2xl">
              <span className="overline">Phantom Residence</span>
              <p className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.18] text-[#FFFFF0]">
                {t.hero.promise}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
